import React, { useState } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { useMapZoom } from './MapBearingContext';
import { TrafficLight, PedestrianLight } from '../regulator/TrafficLight';
import { LightState } from '../../types/protocol';
import type { TrafficSignalNode } from '../../data/guadalajara';

interface TrafficSignalDotProps {
  signal: TrafficSignalNode;
}

const DIRECTION_LABELS: Record<string, string> = {
  forward: 'sentido directo',
  backward: 'sentido inverso',
  both: 'ambos sentidos',
};

const TAG_LABELS: Record<string, string> = {
  highway: 'Tipo',
  'traffic_signals': 'Señal',
  'traffic_signals:direction': 'Dirección',
  crossing: 'Cruce',
  button_operated: 'Pulsador',
  tactile_paving: 'Pavimento táctil',
  'traffic_signals:sound': 'Señal acústica',
  'traffic_signals:vibration': 'Vibración',
  bicycle: 'Bicicleta',
};

export const TrafficSignalDot = React.memo(function TrafficSignalDot({
  signal,
}: TrafficSignalDotProps) {
  const mapZoom = useMapZoom();
  const [open, setOpen] = useState(false);

  const direction = signal.tags['traffic_signals:direction'];
  const isCrossing = signal.tags.crossing === 'traffic_signals';
  const tooltipDir = direction ? ` · ${DIRECTION_LABELS[direction] ?? direction}` : '';
  const tooltipText = `#${signal.id}${isCrossing ? ' (cruce)' : ''}${tooltipDir}`;

  const showDetailed = mapZoom >= 17;

  // Tooltip + popup shared between both render modes
  const tooltip = (
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 whitespace-nowrap pointer-events-none opacity-0 group-hover/dot:opacity-100 transition-opacity z-40">
      <span className="text-[10px] font-mono bg-gray-900/90 text-gray-300 px-1.5 py-0.5 rounded shadow">
        {tooltipText}
      </span>
    </div>
  );

  const popup = open && (
    <div
      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-gray-900/95 backdrop-blur-md border border-gray-600/50 rounded-lg shadow-2xl overflow-hidden z-50"
    >
      <div className="px-3 py-2 border-b border-gray-700/50 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-white">
          Semáforo #{signal.id}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(false); }}
          className="text-gray-500 hover:text-gray-300 text-xs cursor-pointer"
        >
          ✕
        </button>
      </div>
      <div className="px-3 py-2 space-y-1 max-h-48 overflow-y-auto">
        {Object.entries(signal.tags).map(([key, value]) => (
          <div key={key} className="flex justify-between text-[10px] gap-2">
            <span className="text-gray-400 truncate">{TAG_LABELS[key] ?? key}</span>
            <span className="text-white font-mono text-right">{value}</span>
          </div>
        ))}
      </div>
      <div className="px-3 py-1.5 text-[9px] text-gray-600 border-t border-gray-700/50">
        {signal.position[1].toFixed(6)}, {signal.position[0].toFixed(6)}
      </div>
    </div>
  );

  if (showDetailed) {
    const scale = Math.min(2, Math.max(0.3, Math.pow(2, (mapZoom - 18.5) * 0.5)));

    return (
      <Marker
        longitude={signal.position[0]}
        latitude={signal.position[1]}
        anchor="bottom"
      >
        <div className="relative group/dot flex flex-col items-center cursor-pointer" onClick={() => setOpen(o => !o)}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'bottom center' }} className="flex flex-col items-center">
            {isCrossing ? (
              <PedestrianLight red={LightState.OFF} green={LightState.ON} size="sm" />
            ) : (
              <TrafficLight red={LightState.OFF} amber={LightState.OFF} green={LightState.ON} size="sm" />
            )}
            <div className="w-[2px] h-3 bg-gray-500/70 mx-auto" />
            <div className="w-3 h-1 rounded-full bg-black/25 blur-[1px]" />
          </div>
          {tooltip}
          {popup}
        </div>
      </Marker>
    );
  }

  // Zoom < 17: simple dot
  const size = Math.min(14, Math.max(5, (mapZoom - 13) * 2.5 + 5));

  return (
    <Marker
      longitude={signal.position[0]}
      latitude={signal.position[1]}
      anchor="center"
    >
      <div className="relative group/dot">
        <div
          onClick={() => setOpen(o => !o)}
          className="rounded-full cursor-pointer transition-all duration-150 hover:brightness-125"
          style={{
            width: size,
            height: size,
            backgroundColor: isCrossing ? '#f59e0b' : '#22c55e',
            border: `1.5px solid ${isCrossing ? '#b45309' : '#166534'}`,
            boxShadow: '0 0 4px rgba(0,0,0,0.4)',
          }}
        />
        {tooltip}
        {popup}
      </div>
    </Marker>
  );
});
